import VesselFormController from '../vessel_form_controller'

// Prefer Stimulus targets over querySelector/querySelectorAll. Add target attributes in the view
// and use this.hasXTarget / this.xTargets so behaviour stays scoped and maintainable.
export default class extends VesselFormController {
  static targets = [
    'charterAttachedSelect',
    'charterExpiryDateInput',
    'charterRateInput',
    'submitButton',
    'vessel',
    'constructionQualitySelect',
    'monthBuiltField',
    'yearBuiltField',
    'vesselBuiltDateField',
    'futureVesselTooltipTemplate',
    'futureVesselTooltip',
    'futureAssumptionDateInput',
    'customMvResultCell',
    'showSpeedSelect',
    'speedsConsHeaderVessel',
    'speedsConsVesselSection',
    'riskPremiumSelect']

  connect() {
    super.connect()
    this.initDatepicker()
    this.validateCharterAttached()
    this.element.addEventListener('snp-form:validateCharter', () => this.validateCharterAttached())
    this.element.addEventListener('snp-form:disableSubmit', () => this.disableSubmit())
    this.element.addEventListener('snp-form:futureVesselCheck', (e) => this.futureVesselCheck(e.detail?.date))
    this._boundSyncSpeedConsSections = (e) => this.syncSpeedConsSections(e.detail?.anyShown)
    document.addEventListener('snp-speed-cons-toggle:syncSections', this._boundSyncSpeedConsSections)
    this._boundIvDatepickerSelected = (e) => this.updateAssumptionDate(e.detail?.date)
    document.addEventListener('iv-datepicker:dateSelected', this._boundIvDatepickerSelected)
    this.element.addEventListener('snp-form:assumptionDateChanged', (e) => this.updateAssumptionDate(e.detail?.date))
    this.bindConstructionQualitySelect()
    this.bindShowSpeedSelect()
    this.element.addEventListener('snp:highlightCustomResult', () => this.applyCustomHighlight())
    this.futureVesselCheck(this.hasFutureAssumptionDateInputTarget ? this.futureAssumptionDateInputTarget.value : null)
  }

  disconnect() {
    super.disconnect()
    if (this._boundSyncSpeedConsSections) {
      document.removeEventListener('snp-speed-cons-toggle:syncSections', this._boundSyncSpeedConsSections)
    }
    if (this._boundIvDatepickerSelected) {
      document.removeEventListener('iv-datepicker:dateSelected', this._boundIvDatepickerSelected)
    }
  }

  // Override: validate charter state on every change instead of just re-enabling submit
  attachInputListeners() {
    this.element.addEventListener('change', () => this.validateCharterAttached())
    this.element.addEventListener('input', () => this.validateCharterAttached())
  }

  initDatepicker() {
    if (!this.hasCharterExpiryDateInputTarget) return

    $(this.charterExpiryDateInputTarget).datepicker({
      dateFormat: 'dd-mm-yy',
      updateViewDate: true,
      changeMonth: true,
      changeYear: true,
      defaultDate: new Date(),
      onSelect: () =>
          this.charterExpiryDateInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  validateCharterAttached() {
    if (!this.hasCharterExpiryDateInputTarget || !this.hasCharterRateInputTarget || !this.hasCharterAttachedSelectTarget) {
      return
    }

    const dateFieldIsEmpty = this.charterExpiryDateInputTarget.value.trim() === ''
    const rateFieldIsEmpty = this.charterRateInputTarget.value.trim() === ''
    const isCharterAttached = this.charterAttachedSelectTarget.value === 'true'

    this.charterExpiryDateInputTarget.classList.remove('field-error')
    this.charterExpiryDateInputTarget.disabled = false
    this.charterRateInputTarget.classList.remove('field-error')
    this.charterRateInputTarget.disabled = false
    this.enableSubmit()

    if (isCharterAttached && (dateFieldIsEmpty || rateFieldIsEmpty)) {
      if(dateFieldIsEmpty) {
        this.charterExpiryDateInputTarget.classList.add('field-error')
      }
      if(rateFieldIsEmpty) {
        this.charterRateInputTarget.classList.add('field-error')
      }
      this.disableSubmit()
    }
    if (!isCharterAttached) {
      this.charterExpiryDateInputTarget.disabled = true
      this.charterRateInputTarget.disabled = true
    }
  }

  updateAssumptionDate(date) {
    if (!date || !this.hasFutureAssumptionDateInputTarget) return
    this.futureAssumptionDateInputTarget.value = date
  }

  // Shows a tooltip when the vessel build date is in the future relative to the assumption date (triggered by callers with date string "dd-mm-yyyy").
  futureVesselCheck(date) {
    if (!date || !this.hasMonthBuiltFieldTarget || !this.hasYearBuiltFieldTarget || !this.hasVesselBuiltDateFieldTarget) return
    const monthBuilt = parseInt(this.monthBuiltFieldTarget.value, 10) || 0
    const yearBuilt = parseInt(this.yearBuiltFieldTarget.value, 10) || 0
    const [day, month, year] = date.split('-').map((s) => parseInt(s, 10) || 0)
    const isFutureVessel = (yearBuilt > year) || (yearBuilt === year && monthBuilt > month)
    const container = this.vesselBuiltDateFieldTarget
    this.futureVesselTooltipTargets.forEach((el) => el.remove())
    if (isFutureVessel && this.hasFutureVesselTooltipTemplateTarget) {
      container.appendChild(this.futureVesselTooltipTemplateTarget.content.cloneNode(true))
    }
  }

  // Construction quality select: toggle custom-speed-cons class
  bindConstructionQualitySelect() {
    if (!this.hasConstructionQualitySelectTarget) return
    this.constructionQualitySelectTargets.forEach((select) => {
      select.addEventListener('change', () => this.toggleConstructionQualitySelect(select))
      this.toggleConstructionQualitySelect(select)
    })
  }

  toggleConstructionQualitySelect(select) {
    const defaultConstructionQuality = select.dataset.defaultConstructionQuality ?? ''
    const val = select.value
    if (defaultConstructionQuality === '' || String(val) === String(defaultConstructionQuality)) {
      select.classList.remove('custom-speed-cons')
    } else {
      select.classList.add('custom-speed-cons')
    }
  }

  // Speed/consumption toggle: show/hide this form's sections based on "Yes/No" select; syncs with parent for static labels.
  bindShowSpeedSelect() {
    if (!this.hasShowSpeedSelectTarget) return
    this.showSpeedSelectTargets.forEach((select) => {
      select.addEventListener('change', () => {
        this.updateSpeedConsVisibility()
        const forceShow = this.showSpeedSelectTargets.some((s) => s.value === 'true')
        this.element.dispatchEvent(new CustomEvent('snp-form:speedConsToggled', { bubbles: true, detail: { forceShow } }))
      })
    })
    this.updateSpeedConsVisibility()
    this.element.dispatchEvent(new CustomEvent('snp-form:speedConsToggled', { bubbles: true }))
  }

  updateSpeedConsVisibility() {
    if (!this.hasShowSpeedSelectTarget || !this.hasSpeedsConsHeaderVesselTarget || !this.hasSpeedsConsVesselSectionTarget) return
    const show = this.showSpeedSelectTargets.some((s) => s.value === 'true')
    this.speedsConsHeaderVesselTargets.forEach((el) => {
      el.classList.remove('d-none')
      el.classList.toggle('invisible', !show)
    })
    this.speedsConsVesselSectionTargets.forEach((el) => {
      el.classList.remove('d-none')
      el.classList.toggle('invisible', !show)
    })
  }

  applyCustomHighlight() {
    if (!this.hasCustomMvResultCellTarget) return
    const resultCell = this.customMvResultCellTarget
    resultCell.classList.remove('custom-speed-cons')

    let highlight = this.hasConstructionQualitySelectTarget &&
      this.constructionQualitySelectTargets.some(el => el.classList.contains('custom-speed-cons'))

    if (!highlight && this.hasRiskPremiumSelectTarget) {
      highlight = this.riskPremiumSelectTarget.classList.contains('custom-speed-cons')
    }

    if (!highlight && this.hasShowSpeedSelectTarget && this.hasSpeedsConsVesselSectionTarget) {
      const speedShown = this.showSpeedSelectTargets.some(el => el.value === 'true')
      if (speedShown) {
        // target impractical: filters all dynamically-rendered number inputs by class
        highlight = Array.from(this.speedsConsVesselSectionTarget.querySelectorAll('input[type="number"]'))
          .some(el => el.classList.contains('custom-speed-cons'))
      }
    }

    if (highlight) resultCell.classList.add('custom-speed-cons')
  }

  syncSpeedConsSections(anyShown) {
    if (anyShown === undefined) return
    if (!anyShown && this.hasSpeedsConsHeaderVesselTarget && this.hasSpeedsConsVesselSectionTarget) {
      this.speedsConsHeaderVesselTargets.forEach((el) => el.classList.add('d-none'))
      this.speedsConsVesselSectionTargets.forEach((el) => el.classList.add('d-none'))
    } else {
      this.updateSpeedConsVisibility()
    }
  }
}
